import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  ArrowUp, ArrowDown, Plus, Trash, Wallet, TrendUp,
  FunnelSimple, CurrencyDollar, Bank, CreditCard, X,
  UploadSimple, FileText, MagnifyingGlass, Lightning,
  ChartPie, ArrowRight, Sparkle, CaretDown, Check, Warning
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';

const INCOME_CATS = ["Salary", "Rent Income", "Interest", "Business", "Investment Returns", "Gift", "Refund", "Other Income"];
const EXPENSE_CATS = ["Groceries", "Utilities", "Staff Salary", "Maintenance", "Entertainment", "Medical", "Education", "Transport", "Shopping", "Rent Paid", "EMI", "Insurance", "Subscription", "Other Expense"];
const PAYMENT_MODES = ["Cash", "UPI", "Bank Transfer", "Card", "Cheque"];
const COLORS = ['#FF6B35', '#00E5FF', '#22C55E', '#F59E0B', '#A855F7', '#EC4899', '#EF4444', '#14B8A6', '#6366F1', '#84CC16'];
const BANK_CATEGORIES = [
  "Groceries", "Food Delivery", "Shopping Online", "Shopping Offline",
  "Utilities", "Mobile Recharge", "Internet", "Electricity", "Gas", "Water",
  "EMI", "Loan Payment", "Insurance", "Rent", "Salary Credit",
  "Staff Salary", "Maid/Cook/Driver", "School Fees", "Medical",
  "Fuel", "Transport", "Entertainment", "Subscription",
  "UPI Transfer", "ATM Withdrawal", "Bank Charges", "Tax",
  "Investment", "Mutual Fund", "Fixed Deposit",
  "Interest Earned", "Cashback", "Refund", "Gift",
  "Other Income", "Other Expense"
];

export default function CashbookPage() {
  const { api, user } = useAuth();
  const { t } = useLanguage();
  const [mainTab, setMainTab] = useState('ledger');
  const [sourceFilter, setSourceFilter] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [balance, setBalance] = useState(null);
  const [monthlyFlow, setMonthlyFlow] = useState([]);
  const [categoryBreakdown, setCategoryBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState({ month: '', type: '' });
  const [form, setForm] = useState({ type: 'debit', amount: '', category: '', description: '', date: '', payment_mode: 'UPI', reference: '', event_id: '' });
  const [events, setEvents] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter.month) params.set('month', filter.month);
      if (filter.type) params.set('type', filter.type);
      if (sourceFilter) params.set('source_type', sourceFilter);
      const [txnRes, balRes, flowRes, catRes] = await Promise.all([
        api.get(`/cashbook/transactions?${params}`),
        api.get(`/cashbook/balance${filter.month ? `?month=${filter.month}` : ''}`),
        api.get('/cashbook/monthly-flow?months=6'),
        api.get(`/cashbook/category-breakdown${filter.month ? `?month=${filter.month}` : ''}`)
      ]);
      setTransactions(txnRes.data);
      setBalance(balRes.data);
      setMonthlyFlow(flowRes.data);
      setCategoryBreakdown(catRes.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, filter, sourceFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load active events once so users can tag an expense/income to a party
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/events');
        setEvents((r.data || []).filter(e => e.status !== 'closed'));
      } catch (e) { /* silent — events module is optional */ }
    })();
  }, [api]);

  const handleSubmit = async () => {
    if (!form.amount || !form.category) return;
    try {
      await api.post('/cashbook/transactions', { ...form, amount: parseFloat(form.amount) });
      setShowAdd(false);
      setForm({ type: 'debit', amount: '', category: '', description: '', date: '', payment_mode: 'UPI', reference: '', event_id: '' });
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete transaction?')) return;
    try { await api.delete(`/cashbook/transactions/${id}`); fetchData(); } catch (e) { console.error(e); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;

  const mainTabs = [
    { id: 'ledger', label: 'Cashbook' },
    { id: 'statements', label: 'Bank Statements' },
  ];

  return (
    <div data-testid="cashbook-page" className="space-y-6 relative">
      <div className="glow-orb-orange -top-20 -left-20 z-0" />

      {/* Header */}
      <div className="relative z-10 animate-fade-up flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-neutral-500 text-xs sm:text-sm font-medium">Finance</p>
          <h1 className="font-heading text-xl sm:text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Cashbook</h1>
        </div>
        <div className="flex items-center gap-2">
          {mainTab === 'ledger' && (
            <button onClick={() => setShowAdd(true)} data-testid="add-transaction-btn"
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#FF6B35] text-white text-xs font-semibold btn-glow hover:scale-105 transition-transform">
              <Plus size={16} /> Add Entry
            </button>
          )}
        </div>
      </div>

      {/* Main Tabs */}
      <div className="flex gap-1 p-1 rounded-2xl overflow-x-auto relative z-10" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {mainTabs.map(tab => (
          <button key={tab.id} onClick={() => setMainTab(tab.id)} data-testid={`cashbook-tab-${tab.id}`}
            className={`px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap flex-shrink-0 ${mainTab === tab.id ? 'bg-[#FF6B35]/15 text-[#FF6B35]' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}>
            {tab.id === 'statements' && <FileText size={14} className="inline mr-1.5" />}
            {tab.label}
          </button>
        ))}
      </div>

      {mainTab === 'ledger' && (
        <>
          {/* Balance Cards with Bank/Cash */}
          {balance && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-4 relative z-10 animate-fade-up" style={{ animationDelay: '100ms' }}>
              <div className="glass-card p-4 sm:p-5" data-testid="net-balance">
                <p className="overline mb-1">Net Balance</p>
                <p className={`text-xl sm:text-2xl font-heading font-bold ${balance.net_balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  Rs. {Math.abs(balance.net_balance).toLocaleString('en-IN')}
                </p>
                <p className="text-[10px] text-neutral-500 mt-1">{balance.transaction_count} transactions</p>
              </div>
              <div className="glass-card p-4 sm:p-5" data-testid="bank-balance">
                <div className="flex items-center gap-2 mb-1">
                  <Bank size={14} className="text-blue-400" />
                  <p className="overline">Bank</p>
                </div>
                <p className="text-lg sm:text-xl font-heading font-bold text-blue-400">Rs. {(balance.bank_balance || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="glass-card p-4 sm:p-5" data-testid="cash-balance">
                <div className="flex items-center gap-2 mb-1">
                  <Wallet size={14} className="text-green-400" />
                  <p className="overline">Cash</p>
                </div>
                <p className="text-lg sm:text-xl font-heading font-bold text-green-400">Rs. {(balance.cash_balance || 0).toLocaleString('en-IN')}</p>
              </div>
              <div className="glass-card p-4 sm:p-5" data-testid="month-income">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowDown size={14} className="text-green-400" />
                  <p className="overline">Month In</p>
                </div>
                <p className="text-lg sm:text-xl font-heading font-bold text-green-400">Rs. {balance.month_income.toLocaleString('en-IN')}</p>
              </div>
              <div className="glass-card p-4 sm:p-5" data-testid="month-expense">
                <div className="flex items-center gap-2 mb-1">
                  <ArrowUp size={14} className="text-red-400" />
                  <p className="overline">Month Out</p>
                </div>
                <p className="text-lg sm:text-xl font-heading font-bold text-red-400">Rs. {balance.month_expense.toLocaleString('en-IN')}</p>
              </div>
            </div>
          )}

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 relative z-10">
            <div className="glass-card p-5 animate-fade-up" data-testid="cash-flow-chart" style={{ animationDelay: '200ms' }}>
              <div className="flex items-center gap-2 mb-4">
                <TrendUp size={16} className="text-[#00E5FF]" />
                <span className="overline">Cash Flow (6 Months)</span>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={monthlyFlow}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#737373' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#737373' }} />
                  <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: 11 }} />
                  <Bar dataKey="income" fill="#22C55E" radius={[3, 3, 0, 0]} name="Income" />
                  <Bar dataKey="expense" fill="#EF4444" radius={[3, 3, 0, 0]} name="Expense" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="glass-card p-5 animate-fade-up" data-testid="category-chart" style={{ animationDelay: '300ms' }}>
              <div className="flex items-center gap-2 mb-4">
                <CurrencyDollar size={16} className="text-[#FF6B35]" />
                <span className="overline">Expense by Category</span>
              </div>
              {categoryBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={categoryBreakdown} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={70} label={({ category, percent }) => `${category} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                      {categoryBreakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `Rs. ${v.toLocaleString('en-IN')}`} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-neutral-500 text-sm">No expense data</div>
              )}
            </div>
          </div>

          {/* Source Filter + Other Filters */}
          <div className="flex flex-wrap items-center gap-3 relative z-10">
            <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
              {[{ id: '', label: 'All' }, { id: 'bank', label: 'Bank' }, { id: 'cash', label: 'Cash' }].map(s => (
                <button key={s.id} onClick={() => setSourceFilter(s.id)} data-testid={`source-filter-${s.id || 'all'}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${sourceFilter === s.id ? 'bg-[#FF6B35]/15 text-[#FF6B35]' : 'text-neutral-400 hover:text-white'}`}>
                  {s.id === 'bank' && <Bank size={12} className="inline mr-1" />}
                  {s.id === 'cash' && <Wallet size={12} className="inline mr-1" />}
                  {s.label}
                </button>
              ))}
            </div>
            <FunnelSimple size={16} className="text-neutral-500" />
            <input type="month" value={filter.month} onChange={e => setFilter(f => ({ ...f, month: e.target.value }))}
              className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
            <select value={filter.type} onChange={e => setFilter(f => ({ ...f, type: e.target.value }))}
              className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none">
              <option value="">All Types</option>
              <option value="credit">Income</option>
              <option value="debit">Expense</option>
            </select>
          </div>

          {/* Transaction List */}
          <div className="glass-card overflow-hidden relative z-10 animate-fade-up" data-testid="transactions-table" style={{ animationDelay: '400ms' }}>
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-neutral-500 text-xs uppercase tracking-wider border-b border-white/5">
                    <th className="p-4">Date</th>
                    <th className="p-4">Type</th>
                    <th className="p-4">Category</th>
                    <th className="p-4">Description</th>
                    <th className="p-4">Mode</th>
                    <th className="p-4 text-right">Amount</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 ? (
                    <tr><td colSpan="7" className="p-8 text-center text-neutral-500">No transactions yet</td></tr>
                  ) : transactions.map(txn => (
                    <tr key={txn.txn_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="p-4 text-neutral-400">{txn.date}</td>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${txn.type === 'credit' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                          {txn.type === 'credit' ? <ArrowDown size={10} /> : <ArrowUp size={10} />}
                          {txn.type === 'credit' ? 'IN' : 'OUT'}
                        </span>
                      </td>
                      <td className="p-4 text-neutral-300">{txn.category}</td>
                      <td className="p-4 text-neutral-400 max-w-[200px] truncate">{txn.description || '-'}</td>
                      <td className="p-4">
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${txn.payment_mode === 'Cash' ? 'bg-green-500/10 text-green-400' : 'bg-blue-500/10 text-blue-400'}`}>
                          {txn.payment_mode}
                        </span>
                      </td>
                      <td className={`p-4 text-right font-semibold ${txn.type === 'credit' ? 'text-green-400' : 'text-red-400'}`}>
                        {txn.type === 'credit' ? '+' : '-'} Rs. {txn.amount.toLocaleString('en-IN')}
                      </td>
                      <td className="p-4">
                        {user?.role === 'admin' && (
                          <button onClick={() => handleDelete(txn.txn_id)} className="text-neutral-600 hover:text-red-400"><Trash size={14} /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="sm:hidden divide-y divide-white/5">
              {transactions.length === 0 ? (
                <p className="p-6 text-center text-neutral-500 text-sm">No transactions yet</p>
              ) : transactions.map(txn => (
                <div key={txn.txn_id} className="p-3 flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${txn.type === 'credit' ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                    {txn.type === 'credit' ? <ArrowDown size={14} className="text-green-400" /> : <ArrowUp size={14} className="text-red-400" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium truncate">{txn.category}</p>
                      <p className={`text-sm font-bold flex-shrink-0 ${txn.type === 'credit' ? 'text-green-400' : 'text-red-400'}`}>
                        {txn.type === 'credit' ? '+' : '-'}{txn.amount.toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-neutral-500 mt-0.5">
                      <span>{txn.date}</span>
                      <span className={`px-1.5 py-0.5 rounded ${txn.payment_mode === 'Cash' ? 'bg-green-500/10 text-green-400' : 'bg-blue-500/10 text-blue-400'}`}>{txn.payment_mode}</span>
                      {txn.description && <span className="truncate">{txn.description}</span>}
                    </div>
                  </div>
                  {user?.role === 'admin' && (
                    <button onClick={() => handleDelete(txn.txn_id)} className="text-neutral-600 hover:text-red-400 flex-shrink-0 p-1"><Trash size={12} /></button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {mainTab === 'statements' && <BankStatementsTab api={api} />}

      {/* Add Transaction Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <div className="glass-card w-full max-w-md p-6 animate-fade-up" data-testid="add-transaction-modal" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-semibold text-white text-lg">New Transaction</h3>
              <button onClick={() => setShowAdd(false)} className="text-neutral-500 hover:text-white"><X size={20} /></button>
            </div>
            <div className="flex gap-2 mb-4">
              <button onClick={() => setForm(f => ({ ...f, type: 'credit', category: '' }))} data-testid="type-credit"
                className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-colors ${form.type === 'credit' ? 'bg-green-500/20 text-green-400 border border-green-500/30' : 'bg-white/5 text-neutral-400 border border-white/10'}`}>
                <ArrowDown size={14} className="inline mr-1" /> Income
              </button>
              <button onClick={() => setForm(f => ({ ...f, type: 'debit', category: '' }))} data-testid="type-debit"
                className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-colors ${form.type === 'debit' ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-white/5 text-neutral-400 border border-white/10'}`}>
                <ArrowUp size={14} className="inline mr-1" /> Expense
              </button>
            </div>
            <div className="space-y-3">
              <input type="number" placeholder="Amount" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                data-testid="txn-amount" className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                data-testid="txn-category" style={{ colorScheme: 'dark' }}
                className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none">
                <option value="" style={{ color: '#000' }}>Select Category</option>
                {(form.type === 'credit' ? INCOME_CATS : EXPENSE_CATS).map(c => <option key={c} value={c} style={{ color: '#000' }}>{c}</option>)}
              </select>
              <input type="text" placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                data-testid="txn-description" className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
              <div className="grid grid-cols-2 gap-3">
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  className="px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none" />
                <select value={form.payment_mode} onChange={e => setForm(f => ({ ...f, payment_mode: e.target.value }))}
                  data-testid="txn-payment-mode" style={{ colorScheme: 'dark' }}
                  className="px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none">
                  {PAYMENT_MODES.map(m => <option key={m} value={m} style={{ color: '#000' }}>{m}</option>)}
                </select>
              </div>
              {events.length > 0 && (
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1.5 block">
                    Link to Event <span className="text-neutral-600 normal-case tracking-normal">(optional — counts toward that event's budget)</span>
                  </label>
                  <select value={form.event_id || ''} onChange={e => setForm(f => ({ ...f, event_id: e.target.value }))}
                    data-testid="txn-event-link" style={{ colorScheme: 'dark' }}
                    className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none">
                    <option value="" style={{ color: '#000' }}>No event — general transaction</option>
                    {events.map(ev => (
                      <option key={ev.event_id} value={ev.event_id} style={{ color: '#000' }}>
                        {ev.theme_emoji || '🎉'} {ev.title}{ev.event_date ? ` · ${ev.event_date}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <button onClick={handleSubmit} data-testid="submit-transaction"
              className="w-full mt-4 py-3 rounded-xl bg-[#FF6B35] text-white font-semibold text-sm btn-glow">
              Add Transaction
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ========== BANK STATEMENTS TAB ==========
function BankStatementsTab({ api }) {
  const [statements, setStatements] = useState([]);
  const [bankTxns, setBankTxns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [aiInsights, setAiInsights] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedStmt, setSelectedStmt] = useState('');
  const [subTab, setSubTab] = useState('upload');
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [editingCat, setEditingCat] = useState(null);

  const fetchStatements = useCallback(async () => {
    try {
      const res = await api.get('/bank-statements/statements');
      setStatements(res.data);
    } catch (e) { console.error(e); }
  }, [api]);

  const fetchTransactions = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedStmt) params.set('statement_id', selectedStmt);
      params.set('limit', '300');
      const qs = params.toString();
      const [txnRes, analyticsRes] = await Promise.all([
        api.get(`/bank-statements/transactions?${qs}`),
        api.get(`/bank-statements/analytics?${qs}`),
      ]);
      setBankTxns(txnRes.data);
      setAnalytics(analyticsRes.data);
    } catch (e) { console.error(e); }
  }, [api, selectedStmt]);

  useEffect(() => { fetchStatements(); }, [fetchStatements]);
  useEffect(() => { if (statements.length > 0 || selectedStmt) fetchTransactions(); }, [fetchTransactions, statements.length, selectedStmt]);

  const handleUpload = async (file) => {
    setUploading(true);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/bank-statements/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult(res.data);
      fetchStatements();
      fetchTransactions();
      setSubTab('dashboard');
    } catch (e) {
      setUploadResult({ error: e.response?.data?.detail || 'Upload failed' });
    }
    setUploading(false);
  };

  const handleDeleteStmt = async (stmtId) => {
    if (!window.confirm('Delete this statement and all its transactions?')) return;
    try {
      await api.delete(`/bank-statements/statements/${stmtId}`);
      setSelectedStmt('');
      fetchStatements();
      fetchTransactions();
    } catch (e) { console.error(e); }
  };

  const fetchInsights = async () => {
    setLoadingInsights(true);
    try {
      const params = selectedStmt ? `?statement_id=${selectedStmt}` : '';
      const res = await api.get(`/bank-statements/ai-insights${params}`);
      setAiInsights(res.data.insights);
    } catch (e) { setAiInsights('Failed to generate insights.'); }
    setLoadingInsights(false);
  };

  const handleCategoryUpdate = async (txnId, newCat) => {
    try {
      await api.put(`/bank-statements/transactions/${txnId}/category`, { category: newCat });
      setEditingCat(null);
      fetchTransactions();
    } catch (e) { console.error(e); }
  };

  const subTabs = [
    { id: 'upload', label: 'Upload' },
    { id: 'dashboard', label: 'Spending Dashboard' },
    { id: 'transactions', label: 'Transactions' },
    { id: 'insights', label: 'AI Insights' },
  ];

  return (
    <div className="space-y-5" data-testid="bank-statements-tab">
      {/* Sub-tabs */}
      <div className="flex gap-1 p-1 rounded-2xl overflow-x-auto" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
        {subTabs.map(tab => (
          <button key={tab.id} onClick={() => { setSubTab(tab.id); if (tab.id === 'insights' && !aiInsights) fetchInsights(); }}
            data-testid={`stmt-tab-${tab.id}`}
            className={`px-3 sm:px-4 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition-all whitespace-nowrap flex-shrink-0 ${subTab === tab.id ? 'bg-blue-500/15 text-blue-400' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Statement selector */}
      {statements.length > 0 && subTab !== 'upload' && (
        <div className="flex items-center gap-3 flex-wrap">
          <select value={selectedStmt} onChange={e => setSelectedStmt(e.target.value)}
            data-testid="statement-selector"
            className="px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white outline-none min-w-[200px]">
            <option value="">All Statements</option>
            {statements.map(s => (
              <option key={s.statement_id} value={s.statement_id}>{s.filename} ({s.transaction_count} txns)</option>
            ))}
          </select>
          {selectedStmt && (
            <button onClick={() => handleDeleteStmt(selectedStmt)}
              className="px-3 py-2 rounded-xl bg-red-500/10 text-red-400 text-xs font-semibold hover:bg-red-500/20">
              <Trash size={14} className="inline mr-1" /> Delete
            </button>
          )}
        </div>
      )}

      {/* Upload Sub-tab */}
      {subTab === 'upload' && (
        <div className="space-y-4">
          <div
            className="glass-card p-8 sm:p-12 text-center border-2 border-dashed border-white/10 hover:border-[#FF6B35]/30 transition-colors cursor-pointer"
            data-testid="upload-dropzone"
            onClick={() => document.getElementById('stmt-file-input').click()}
            onDragOver={e => e.preventDefault()}
            onDrop={e => { e.preventDefault(); if (e.dataTransfer.files[0]) handleUpload(e.dataTransfer.files[0]); }}
          >
            <input id="stmt-file-input" type="file" accept=".csv,.pdf" className="hidden"
              onChange={e => { if (e.target.files[0]) handleUpload(e.target.files[0]); }} />
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-neutral-400">Parsing your bank statement with AI...</p>
              </div>
            ) : (
              <>
                <UploadSimple size={40} className="text-neutral-600 mx-auto mb-3" />
                <p className="text-base font-semibold text-white mb-1">Upload Bank Statement</p>
                <p className="text-sm text-neutral-500">Drag & drop or click to upload PDF or CSV</p>
                <p className="text-xs text-neutral-600 mt-2">Supports ICICI, SBI, HDFC, Axis and most Indian banks</p>
              </>
            )}
          </div>

          {uploadResult && (
            <div className={`glass-card p-4 ${uploadResult.error ? 'border border-red-500/30' : 'border border-green-500/30'}`} data-testid="upload-result">
              {uploadResult.error ? (
                <p className="text-sm text-red-400"><Warning size={16} className="inline mr-1" />{uploadResult.error}</p>
              ) : (
                <div>
                  <p className="text-sm text-green-400 font-semibold"><Check size={16} className="inline mr-1" />{uploadResult.message}</p>
                  <div className="flex gap-4 mt-2 text-xs text-neutral-400">
                    <span>Total Debits: Rs. {(uploadResult.total_debit || 0).toLocaleString('en-IN')}</span>
                    <span>Total Credits: Rs. {(uploadResult.total_credit || 0).toLocaleString('en-IN')}</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Uploaded Statements List */}
          {statements.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-white mb-3">Uploaded Statements</h3>
              <div className="space-y-2">
                {statements.map(stmt => (
                  <div key={stmt.statement_id} className="glass-card p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText size={20} className="text-blue-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-white truncate">{stmt.filename}</p>
                        <div className="flex gap-3 text-[10px] text-neutral-500 mt-0.5">
                          <span>{stmt.transaction_count} transactions</span>
                          <span>Debit: Rs. {(stmt.total_debit || 0).toLocaleString('en-IN')}</span>
                          <span>Credit: Rs. {(stmt.total_credit || 0).toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                    </div>
                    <button onClick={() => { setSelectedStmt(stmt.statement_id); setSubTab('dashboard'); }}
                      className="px-3 py-1.5 rounded-xl bg-blue-500/10 text-blue-400 text-xs font-semibold flex-shrink-0">
                      View <ArrowRight size={12} className="inline ml-1" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Spending Dashboard */}
      {subTab === 'dashboard' && analytics && (
        <div className="space-y-5" data-testid="spending-dashboard">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="glass-card p-4">
              <p className="overline mb-1">Total Spent</p>
              <p className="text-lg font-bold text-red-400">Rs. {(analytics.summary?.total_debit || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className="glass-card p-4">
              <p className="overline mb-1">Total Received</p>
              <p className="text-lg font-bold text-green-400">Rs. {(analytics.summary?.total_credit || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className="glass-card p-4">
              <p className="overline mb-1">Avg Daily Spend</p>
              <p className="text-lg font-bold text-yellow-400">Rs. {(analytics.summary?.avg_daily_spend || 0).toLocaleString('en-IN')}</p>
            </div>
            <div className="glass-card p-4">
              <p className="overline mb-1">Transactions</p>
              <p className="text-lg font-bold text-white">{analytics.summary?.total_transactions || 0}</p>
            </div>
          </div>

          {/* Category Pie + Breakdown */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="glass-card p-5">
              <div className="flex items-center gap-2 mb-4">
                <ChartPie size={16} className="text-[#FF6B35]" />
                <span className="overline">Spending by Category</span>
              </div>
              {analytics.category_breakdown?.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={analytics.category_breakdown.slice(0, 10)} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={90} label={({ category, percentage }) => `${category} ${percentage}%`} labelLine={false}>
                      {analytics.category_breakdown.slice(0, 10).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => `Rs. ${v.toLocaleString('en-IN')}`} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-[250px] text-neutral-500 text-sm">No spending data</div>
              )}
            </div>

            <div className="glass-card p-5">
              <div className="flex items-center gap-2 mb-4">
                <CurrencyDollar size={16} className="text-[#00E5FF]" />
                <span className="overline">Category Breakdown</span>
              </div>
              <div className="space-y-2 max-h-[250px] overflow-y-auto">
                {(analytics.category_breakdown || []).map((cat, i) => (
                  <div key={cat.category} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="text-xs text-neutral-300 flex-1 truncate">{cat.category}</span>
                    <span className="text-xs text-neutral-500">{cat.percentage}%</span>
                    <span className="text-xs font-semibold text-white">Rs. {cat.amount.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Monthly Trend */}
          {analytics.monthly_trend?.length > 1 && (
            <div className="glass-card p-5">
              <div className="flex items-center gap-2 mb-4">
                <TrendUp size={16} className="text-[#22C55E]" />
                <span className="overline">Monthly Spending Trend</span>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={analytics.monthly_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#737373' }} />
                  <YAxis tick={{ fontSize: 10, fill: '#737373' }} />
                  <Tooltip contentStyle={{ background: '#1a1a1a', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, fontSize: 11 }} />
                  <Line type="monotone" dataKey="expense" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} name="Expense" />
                  <Line type="monotone" dataKey="income" stroke="#22C55E" strokeWidth={2} dot={{ r: 3 }} name="Income" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Top Merchants */}
          {analytics.top_merchants?.length > 0 && (
            <div className="glass-card p-5">
              <span className="overline block mb-3">Top Merchants / Payees</span>
              <div className="space-y-2">
                {analytics.top_merchants.slice(0, 10).map((m, i) => (
                  <div key={i} className="flex items-center gap-3 py-1">
                    <span className="text-[10px] text-neutral-600 w-5 text-right">{i + 1}</span>
                    <span className="text-xs text-neutral-300 flex-1 truncate">{m.description}</span>
                    <span className="text-[10px] text-neutral-500">{m.count}x</span>
                    <span className="text-xs font-semibold text-red-400">Rs. {m.total.toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Patterns */}
          {analytics.patterns?.length > 0 && (
            <div className="glass-card p-5">
              <div className="flex items-center gap-2 mb-3">
                <Lightning size={16} className="text-yellow-400" />
                <span className="overline">Detected Patterns</span>
              </div>
              <div className="space-y-3">
                {analytics.patterns.map((p, i) => (
                  <div key={i} className="p-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${p.type === 'recurring' ? 'bg-blue-500/10 text-blue-400' : p.type === 'spike' ? 'bg-red-500/10 text-red-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                        {p.type}
                      </span>
                    </div>
                    <p className="text-xs text-neutral-300">{p.insight}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {subTab === 'dashboard' && !analytics?.summary?.total_transactions && (
        <div className="glass-card p-10 text-center">
          <FileText size={40} className="text-neutral-600 mx-auto mb-3" />
          <p className="text-sm text-neutral-500">Upload a bank statement first to see your spending dashboard</p>
        </div>
      )}

      {/* Transactions List */}
      {subTab === 'transactions' && (
        <div className="glass-card overflow-hidden" data-testid="bank-transactions-list">
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-neutral-500 text-xs uppercase tracking-wider border-b border-white/5">
                  <th className="p-4">Date</th>
                  <th className="p-4">Description</th>
                  <th className="p-4">Category</th>
                  <th className="p-4 text-right">Debit</th>
                  <th className="p-4 text-right">Credit</th>
                  <th className="p-4 text-right">Balance</th>
                </tr>
              </thead>
              <tbody>
                {bankTxns.length === 0 ? (
                  <tr><td colSpan="6" className="p-8 text-center text-neutral-500">No transactions. Upload a statement first.</td></tr>
                ) : bankTxns.map(txn => (
                  <tr key={txn.txn_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-4 text-neutral-400 whitespace-nowrap">{txn.date}</td>
                    <td className="p-4 text-neutral-300 max-w-[250px] truncate">{txn.description}</td>
                    <td className="p-4">
                      {editingCat === txn.txn_id ? (
                        <select autoFocus value={txn.category} onChange={e => handleCategoryUpdate(txn.txn_id, e.target.value)}
                          onBlur={() => setEditingCat(null)}
                          className="px-2 py-1 rounded bg-white/10 border border-white/20 text-xs text-white outline-none">
                          {BANK_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      ) : (
                        <button onClick={() => setEditingCat(txn.txn_id)}
                          className="text-xs text-neutral-400 hover:text-[#FF6B35] transition-colors" title="Click to change category">
                          {txn.category} {txn.category_manual && <Check size={10} className="inline text-green-400" />}
                        </button>
                      )}
                    </td>
                    <td className="p-4 text-right text-red-400 font-medium">{txn.debit > 0 ? `Rs. ${txn.debit.toLocaleString('en-IN')}` : '-'}</td>
                    <td className="p-4 text-right text-green-400 font-medium">{txn.credit > 0 ? `Rs. ${txn.credit.toLocaleString('en-IN')}` : '-'}</td>
                    <td className="p-4 text-right text-neutral-300">{txn.balance > 0 ? `Rs. ${txn.balance.toLocaleString('en-IN')}` : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="sm:hidden divide-y divide-white/5">
            {bankTxns.length === 0 ? (
              <p className="p-6 text-center text-neutral-500 text-sm">No transactions</p>
            ) : bankTxns.map(txn => (
              <div key={txn.txn_id} className="p-3">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-neutral-200 truncate flex-1">{txn.description}</p>
                  <p className={`text-sm font-bold flex-shrink-0 ${txn.debit > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {txn.debit > 0 ? `-${txn.debit.toLocaleString('en-IN')}` : `+${txn.credit.toLocaleString('en-IN')}`}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-neutral-500 mt-1">
                  <span>{txn.date}</span>
                  <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">{txn.category}</span>
                  {txn.balance > 0 && <span>Bal: Rs. {txn.balance.toLocaleString('en-IN')}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Insights */}
      {subTab === 'insights' && (
        <div className="glass-card p-5" data-testid="ai-insights">
          <div className="flex items-center gap-2 mb-4">
            <Sparkle size={18} weight="fill" className="text-[#FF6B35]" />
            <span className="text-sm font-bold text-white">AI Spending Insights</span>
            <button onClick={fetchInsights} disabled={loadingInsights}
              className="ml-auto px-3 py-1.5 rounded-xl bg-[#FF6B35]/10 text-[#FF6B35] text-xs font-semibold hover:bg-[#FF6B35]/20">
              {loadingInsights ? 'Analyzing...' : 'Refresh'}
            </button>
          </div>
          {loadingInsights ? (
            <div className="flex items-center gap-3 py-8 justify-center">
              <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
              <span className="text-sm text-neutral-400">AI is analyzing your spending patterns...</span>
            </div>
          ) : aiInsights ? (
            <div className="prose prose-invert prose-sm max-w-none">
              <pre className="text-sm text-neutral-300 whitespace-pre-wrap leading-relaxed font-sans">{aiInsights}</pre>
            </div>
          ) : (
            <p className="text-sm text-neutral-500 py-6 text-center">Upload a bank statement to get AI-powered spending insights</p>
          )}
        </div>
      )}
    </div>
  );
}
